import React from 'react';
import { PropTypes } from 'prop-types';
import DateTime from 'react-datetime';
import { date, object, array, number, string } from 'yup';
import moment from "moment-timezone";
import { forOwn } from 'lodash';

import ApiCollection from '../../utility/api-collection';
import TDModal from '../shared/td-modal';
import StatePicker from '../../utility/state-picker';

class NewIncentive extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      creatingIncentives: false,
      errorMessages:      []
    };

    this.createIncentive = this.createIncentive.bind(this);
  }

  componentDidMount() {
    const { store } = this.context;
    this.subscription = store.subscribe(() => {
      const { providerIncentiveCost: { newIncentive } } = store.getState();
      const { saving, creatingIncentives, errors, error_record } = newIncentive;

      const errorKeyMap = {
        date_range: 'Date Range'
      };
      if (errors) {
        let errorMessages = [];
        forOwn(errors, (value, key) => {
          if(value) {
            errorMessages = errorMessages.concat(
              value.map(errorMessage => `${errorKeyMap[key]} for (${error_record.state_cd}): ${errorMessage}`)
            );
          };
        });
        this.setState({ errors, errorMessages });
      } else {
        this.setState({ errors: undefined, errorMessages: [] });
      }
      this.setState({ creatingIncentives, saving });
    });
  }

  componentWillUnmount() {
    this.subscription();
  }

  getValues() {
    const {
      serviceSpecialtyCode,
      startDateTimePicker,
      endDateTimePicker,
      amountInput,
      maxNumberOfProvidersInput,
      statePicker
    } = this.refs;

    const selectedSpecialtyCode = serviceSpecialtyCode.refs.specialtySelect.value;
    const selectedStartDate = startDateTimePicker.state.selectedDate;
    const startDateTimeMoment = selectedStartDate ? moment(startDateTimePicker.state.selectedDate).tz("America/Chicago", true) : undefined;
    const selectedEndDate = endDateTimePicker.state.selectedDate;
    const endDateTimeMoment = selectedEndDate ? moment(endDateTimePicker.state.selectedDate).tz("America/Chicago", true) : undefined;
    const maxNumberOfProviders = parseInt(maxNumberOfProvidersInput.value, 10) || undefined;
    const states = statePicker.values();
    let amount = undefined;
    if (!isNaN(parseInt(amountInput.value, 10))) {
      amount = parseInt(amountInput.value, 10);
    }

    let startDateTime;
    if (startDateTimeMoment) {
      startDateTime = startDateTimeMoment.toDate();
    }

    let endDateTime;
    if (endDateTimeMoment) {
      endDateTime = endDateTimeMoment.toDate();
    }

    return {
      startDateTime,
      endDateTime,
      amount,
      maxNumberOfProviders,
      selectedSpecialtyCode,
      states
    };
  }

  createIncentive() {
    const values = this.getValues();
    const schema = object().shape({
      amount: number().required('Amount is required')
        .min(0, 'Amount must be greater or equal to 0')
        .integer(),
      maxNumberOfProviders: number().required('Max Number of Providers is required')
        .positive('Max Number of Providers must be greater than 0')
        .integer(),
      startDateTime: date().required('Start Time Required')
        .min(moment.tz("America/Chicago").toDate(), 'Start date must be after now'),
      endDateTime: date().required('End Time Required')
        .min(values.startDateTime ||  moment.tz("America/Chicago").toDate(), 'End Date must come after Start Date'),
        selectedSpecialtyCode: string().required('Speciality is required'),
      states: array().required('State is a required field')
        .min(1)
    });

    schema.validate(values).then(_isValid => {
      this.setState({ errorMessage: undefined }, () => {
        const incentives = values.states.map(stateCode => {
          const incentive = {
            ...values,
            stateCode,
            startDateTime: moment(values.startDateTime).tz('America/Chicago').format("YYYY/MM/DD hh:mm:ss A z"),
            endDateTime: moment(values.endDateTime).tz('America/Chicago').format("YYYY/MM/DD hh:mm:ss A z")
          };
          incentive.states = undefined;
          return incentive;
        });
        this.context.addManyIncentives(incentives);
      });
    }).catch(err => {
      this.setState({ formErrorMessage: err.message });
    });
  }


  renderCreateErrors() {
    const { errorMessages } = this.state;
    if (errorMessages.length > 0) {
      let idx = 0;
      return (
        <ul className="errors">
          { errorMessages.map(message => <li key={idx++}>{message}</li>) }
        </ul>
      );
    }
    return null;
  }

  renderFormErrors() {
    const { formErrorMessage } = this.state;
    if (formErrorMessage) {
      return (
        <div>{formErrorMessage}</div>
      );
    }
    return null;
  }

  render() {
    const { saving, creatingIncentives } = this.state;
    const { startAddManyIncentives, finishAddManyIncentives } = this.context;
    if (!creatingIncentives) {
      return (
        <div id="new-incentive">
          <div onClick={() => startAddManyIncentives()} className="button">Create Incentive</div>
        </div>
      );
    }
    let createButtonText = 'Create';
    let createButtonClassName = 'button';
    if (saving) {
      createButtonText = 'Saving...';
      createButtonClassName = 'button disabled';
    }
    return (
      <TDModal isOpen title="Add Incentive" notifyOfClose={() => this.setState({formErrorMessage: [], errorMessages: []}, () => finishAddManyIncentives())}>
        <div id="new-incentive">
          <div className="content-container">
            { this.renderCreateErrors() }
            { this.renderFormErrors() }

            <ApiCollection endpoint='/v4/ref_service_specialty/consult_service_offering' ref="serviceSpecialtyCode">
                { (specialities) => {
                  return (
                    <div className='filter'>
                      <label htmlFor="serviceSpeciality" style={{display: 'block'}}>
                        For service specialty
                      </label>
                      <select
                        name='serviceSpecialtyCode'
                        id='serviceSpecialtyCode'
                        ref="specialtySelect"
                        defaultValue="GENMEDICAL"
                      >
                        { specialities.ref_service_specialty.map ( specialty => (
                          <option
                            value={specialty.service_specialty_code}
                            key={specialty.service_specialty_code}
                          >
                            { specialty.service_specialty_name }
                          </option>
                        ))}
                      </select>
                    </div>
                  )
                }}
              </ApiCollection>
            <div className="filter">
              <label>
                Start Date/Time
                <DateTime
                  ref="startDateTimePicker"
                  onBlur={() => true}
                />
              </label>
            </div>
            <div className="filter">
              <label>
                End Date/Time
                <DateTime
                  ref="endDateTimePicker"
                  onBlur={() => true}
                />
              </label>
            </div>
            <div className="filter">
              <label>
                Amount<br />
                <input type="number" ref="amountInput" min="0"/>
              </label>
            </div>
            <div className="filter">
              <label>
                Max Number of Providers<br />
                <input type="number" ref="maxNumberOfProvidersInput" min="1"/>
              </label>
            </div>
            <div className="filter">
              <label>
                Location<br />
                <StatePicker multiple ref="statePicker" id='new-incentive-state-selection'/>
              </label>
            </div>
            <div className={createButtonClassName} onClick={this.createIncentive}>
              {createButtonText}
            </div>
          </div>
        </div>
      </TDModal>
    );
  }
}

export default NewIncentive;
NewIncentive.contextTypes = {
  addManyIncentives:       PropTypes.func,
  startAddManyIncentives:  PropTypes.func,
  finishAddManyIncentives: PropTypes.func,
  store:                   PropTypes.object
};
